import PropTypes from "prop-types";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
    LoadingContext,
    M_StateActions,
    MTable,
} from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the Route-Lane relation CRUD page in a tab.
 */
const ROUTE_LANE_CRUD_ROUTE = "/home/routes/routeLaneConfigCrud";

//endregion

//region Component

/**
 * Renders the Route-Lane relations configuration tab.
 *
 * Responsibilities:
 * - render the common configuration-page layout in tabs mode;
 * - keep the page visually aligned with other Config tabs through config-page.css;
 * - render the RouteLaneRels MTable;
 * - replace default MTable View/Edit actions with tab-based navigation;
 * - persist the current menu and selected route-lane relation before opening tabs.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @param {object|null|undefined} props.currentMenu - Current menu context.
 * @returns {JSX.Element} Route-Lane relations configuration tab.
 */
export default function RouteLaneConfig({
    roles,
    onCreateTab,
    toastMessages,
    currentMenu,
}) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [refresh, setRefresh] = useState(false);

    /**
     * Removes table actions that are not required in this tab.
     *
     * Important:
     * - Create and Delete are intentionally disabled for this relation table;
     * - Copy is disabled to avoid duplicating route-lane relations accidentally;
     * - Print is not part of the desired workflow.
     */
    const removeContextItems = useMemo(
        () => ["Print", "Copy", "Delete", "Create"],
        []
    );

    /**
     * Opens the Route-Lane CRUD tab for the requested operation.
     *
     * Flow:
     * - persists the current menu in M_State;
     * - persists the operation in M_State;
     * - validates row selection;
     * - persists the selected route-lane relation row;
     * - opens the configured CRUD route with a contextual tab label.
     *
     * @param {"Edit"|"View"} operation - Requested operation.
     * @param {object|null|undefined} row - Selected route-lane relation row.
     * @returns {void}
     */
    const openRouteLaneCrud = useCallback(
        (operation, row) => {
            dispatch(
                M_StateActions.addM_State({
                    key: "menu",
                    value: currentMenu,
                })
            );

            dispatch(
                M_StateActions.addM_State({
                    key: "operation",
                    value: operation,
                })
            );

            if (!row) {
                toastMessages("error", "", t("SelectedLine"));
                return;
            }

            dispatch(
                M_StateActions.addM_State({
                    key: "RouteLaneLine",
                    value: row,
                })
            );

            const safeRoute = row?.Route ?? "";
            const safeLane = row?.Lane ?? "";

            const tabLabel =
                operation === "View"
                    ? `${t("LabelViewRouteLane")} (${safeRoute} - ${safeLane})`
                    : `${t("LabelEditRouteLane")} (${safeRoute} - ${safeLane})`;

            onCreateTab(ROUTE_LANE_CRUD_ROUTE, tabLabel);
        },
        [currentMenu, dispatch, onCreateTab, t, toastMessages]
    );

    /**
     * Replaces MTable default CRUD handlers with the tab-based Route-Lane workflow.
     *
     * Important:
     * - Create and Delete are intentionally omitted and also removed from the table actions.
     */
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ["View", (row) => openRouteLaneCrud("View", row)],
                ["Edit", (row) => openRouteLaneCrud("Edit", row)],
            ]),
        [openRouteLaneCrud]
    );

    return (
        <div
            className="config-page config-page--tabs"
            style={{ "--config-page-title-color": ctx.secondaryColor }}
        >
            <div className="config-page__header config-page__header--tabs">
                <div className="config-page__title config-page__title--tabs">
                    {t("RouteLaneRelations")}
                </div>

                <div className="config-page__description config-page__description--tabs">
                    {t("InfoRouteLaneRelations")}
                </div>
            </div>

            <div className="config-page__table config-page__table--tabs">
                <MTable
                    filtersOnTable
                    entityName="RouteLaneRels"
                    roleName="Locations"
                    rolePermissions={roles?.map((role) => role.Value)}
                    removeContextItems={removeContextItems}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    useSessionStorage={false}
                    replaceFunctions={replaceFunctions}
                    tabsRailWidth={250}
                />
            </div>
        </div>
    );
}

//endregion

//region PropTypes

RouteLaneConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func,
    currentMenu: PropTypes.any,
};

//endregion